/**
 * FAQAccordionCard - Expandable FAQ accordion.
 */
import React, { useState } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQAccordionCardProps {
  variant?: string;
  title?: string;
  subtitle?: string;
  faqs?: FAQItem[];
  items?: FAQItem[];
  // Single-item convenience props
  question?: string;
  answer?: string;
  allowMultiple?: boolean;
  className?: string;
}

const FAQAccordionCard: React.FC<FAQAccordionCardProps> = (props) => {
  const {
    title,
    subtitle,
    allowMultiple = false,
    className = '',
  } = props;
  const faqs: FAQItem[] = props.faqs || props.items ||
    (props.question ? [{ question: props.question, answer: props.answer || '' }] : []);
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());

  const toggle = (idx: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        if (!allowMultiple) next.clear();
        next.add(idx);
      }
      return next;
    });
  };

  // Single-question convenience mode renders as a bare accordion row so a
  // parent section controls layout; consecutive rows sit flush together.
  const single = !!props.question && faqs.length === 1;

  return (
    <section className={`${single ? '' : 'layout-container section-padding'} ${className}`} aria-labelledby="faq-heading">
      {title && (
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-12)' }}>
          <h2 id="faq-heading" className="heading-lg">{title}</h2>
          {subtitle && <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-4)', maxWidth: '600px', margin: 'var(--spacing-4) auto 0' }}>{subtitle}</p>}
        </div>
      )}

      <div
        style={{ maxWidth: '720px', margin: '0 auto' }}
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {faqs.map((faq, idx) => {
          const isOpen = openIndices.has(idx);
          const headingId = `faq-q-${idx}`;
          const contentId = `faq-a-${idx}`;
          return (
            <div
              key={idx}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              style={{ borderBottom: '1px solid var(--color-border)' }}
            >
              <h3 style={{ margin: 0 }}>
                <button
                  id={headingId}
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                  onClick={() => toggle(idx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--spacing-5) 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'var(--font-family)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-text)',
                    gap: 'var(--spacing-4)',
                  }}
                  itemProp="name"
                >
                  {faq.question}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ flexShrink: 0, transition: 'transform var(--transition-base)', transform: isOpen ? 'rotate(180deg)' : 'none', color: 'var(--color-primary)' }}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </h3>
              <div
                id={contentId}
                role="region"
                aria-labelledby={headingId}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
                style={{
                  maxHeight: isOpen ? '1000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height var(--transition-slow)',
                }}
              >
                <p
                  itemProp="text"
                  style={{
                    color: 'var(--color-text-muted)',
                    paddingBottom: 'var(--spacing-5)',
                    lineHeight: 'var(--line-height-relaxed)',
                  }}
                >
                  {faq.answer}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FAQAccordionCard;
